import { Injectable } from '@angular/core';
import { DwAttributeType, DwContextDefinition, DwControlType, DwCustomContextBase, DwCustomContextDefinition, DwOrmDataService, DwSecurityTopics } from '@devwareapps/devware-cap';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppMetaDataItemNames } from '../../../meta-data/app-meta-data.service';
import { PilexosUserTypeIds } from '../../shared/models/pilexos-user-type.enum';
import { AdminManagerService } from '../../shared/services/admin-manager.service';
import { FlightSchoolExaminerTrainingStatus } from '../models/flight-school-examiner-training-status.model';
import { SchoolRepositoryService } from '../services/school-repository.service';

export const FlightSchoolExaminerTrainingContext = 'FlightSchoolExaminerTrainingContext';

@DwCustomContextDefinition({
    keyName: FlightSchoolExaminerTrainingContext,
    display: 'Flight School Examiner Training Context',
    isGlobal: false,
    parentItem: AppMetaDataItemNames.PlanOfAction,
})
@Injectable()
export class FlightSchoolExaminerTrainingContextService extends DwCustomContextBase {

    constructor(
        private adminManagerService: AdminManagerService,
        private schoolRepositoryService: SchoolRepositoryService
    ) {
        super();
    }

    setupContextDefinition(contextDef: DwContextDefinition) {
        contextDef.getCustomContextFunction = () => this.getCustomContext();
        contextDef.cacheInvalidationTopics = [DwSecurityTopics.SECURITY_CHANGED];
        contextDef.cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.FlightSchoolOrganization));
        contextDef.cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.FlightSchoolExaminer));
        contextDef.cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.TrainingRecord));
        contextDef.cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.TrainingType));
        contextDef.cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.DwUser));
        contextDef.attributes = [];

        this.addProperty(contextDef.attributes, 'ValidateExaminerTrainingForPOA', 'Validate Examiner Training For POA', DwAttributeType.YesNo, DwControlType.Checkbox);
        this.addProperty(contextDef.attributes, 'UserTypeId', 'User Type', DwAttributeType.Integer, DwControlType.NumericTextBox);
        this.addProperty(contextDef.attributes, 'FlightSchoolExaminerId', 'Flight School Examiner', DwAttributeType.Integer, DwControlType.NumericTextBox);
        this.addProperty(contextDef.attributes, 'FlightSchoolExaminerTypeId', 'Flight School Examiner Type', DwAttributeType.Integer, DwControlType.NumericTextBox);
        this.addProperty(contextDef.attributes, 'IsTrainingValidationApplicable', 'Is Training Validation Applicable', DwAttributeType.YesNo, DwControlType.Checkbox);
        this.addProperty(contextDef.attributes, 'HasMetAllRequiredTraining', 'Has Met All Required Training', DwAttributeType.YesNo, DwControlType.Checkbox);
        this.addProperty(contextDef.attributes, 'MissingTrainingTypeIds', 'Missing Training Type Ids', DwAttributeType.Integer, DwControlType.NumericTextBox);
        this.addProperty(contextDef.attributes, 'MissingTrainingTypeNames', 'Missing Training Type Names', DwAttributeType.Text, DwControlType.Textbox);
    }

    getCustomContext() {
        const pilexosUser = this.adminManagerService.getPilexosUser();
        const validUserTypes = [PilexosUserTypeIds.flightInstructor, PilexosUserTypeIds.checkInstructor];

        if (!pilexosUser?.user?.UserId || !validUserTypes.includes(pilexosUser?.UserTypeId)) {
            return of(this.buildDefaultResult(pilexosUser?.UserTypeId));
        }

        return this.schoolRepositoryService.getFlightSchoolExaminerTrainingStatus()
            .pipe(map(result => ({
                ...this.buildDefaultResult(pilexosUser?.UserTypeId),
                ...result
            } as FlightSchoolExaminerTrainingStatus)));
    }

    private buildDefaultResult(userTypeId?: number): FlightSchoolExaminerTrainingStatus {
        return {
            UserTypeId: userTypeId,
            IsTrainingValidationApplicable: false,
            HasMetAllRequiredTraining: true,
            MissingTrainingTypeIds: [],
            MissingTrainingTypeNames: []
        };
    }
}
